<template>
  <div v-if="pageData">
    <div class="container bg-white mx-auto p-0">
      <Hero
        breadcrumbCategoryTitle="Learn"
        breadcrumbCategoryLink="/learn"
        breadcrumbTitle="Lawn Care Guides"
        breadcrumbLink="/learn/lawn-care-guides"
        :heroImageLink="heroImage"
        :title="pageData.title"
        :text="pageData.description"
        color="Green"
        isBasicImage="Y"
      />
    </div>
    <div class="relative">
      <div class="container bg-white mx-auto pt-4">
        <h2 class="text-center mt-0 mb-4">Explore Our Guides</h2>
        <div class="grid md:grid-cols-3 md:gap-8">
          <span v-for="(topic, index) in topics" :key="index">
            <div class="grid grid-cols-1 text-center">
              <img
                loading="lazy"
                v-if="topic.image !== null"
                :src="`${imageBaseUrl}${topic.image}`"
                :alt="topic.name"
              />
              <h2 class="mt-4">
                <NuxtLink :to="`/learn/lawn-care-guides/topics/${topic.slug}/`">{{ topic.name }}</NuxtLink>
              </h2>
              <div class="mt-4" v-html="topic.description" />
              <NuxtLink
                :to="`/learn/lawn-care-guides/topics/${topic.slug}/`"
                class="button w-6/12 mx-auto mb-12 mt-4"
              >
                Learn More
              </NuxtLink>
            </div>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const nuxtApp = useNuxtApp();
const { $sg, $metadata, $userContext } = nuxtApp;

const runtimeConfig = useRuntimeConfig();
const frontEndUrl = (runtimeConfig.public?.frontEndUrl || 'https://www.spring-green.com').replace(/\/$/, '');
const imageBaseUrl = runtimeConfig.public?.sgCmsImage || '';

const [{ data: pageData }, { data: topics }, { data: orgData }] = await Promise.all([
  useAsyncData(
    'lawn-care-guides-landing',
    () => $sg.getOctoberData('lawn-care-guides/landingpagecontent'),
    { default: () => ({}) }
  ),
  useAsyncData(
    'lawn-care-guides-topics',
    () => $sg.getOctoberData('lawn-care-topics'),
    { default: () => [] }
  ),
  useAsyncData('lawn-care-guides-org', () =>
    $metadata?.getOrganizationStructuredData?.() ?? Promise.resolve(null)
  ),
]);

const heroImage = computed(() => {
  const imagePath = pageData.value?.image?.url;
  return imagePath ? `${imageBaseUrl}${imagePath}` : undefined;
});

const topicsItemList = computed(() => {
  if (!topics.value?.length) return null;

  return buildItemListSchema({
    name: 'Lawn Care Guides',
    description: pageData.value?.description,
    items: topics.value.map((topic: any, index: number) => ({
      name: topic.name,
      url: `${frontEndUrl}/learn/lawn-care-guides/topics/${topic.slug}/`,
      position: index + 1,
    })),
  });
});

const seoTitle = computed(() =>
  pageData.value?.seo_title ||
  pageData.value?.title ||
  'Local Lawn Care Services & Weed Control | Spring-Green'
);

const seoDescription = computed(() =>
  pageData.value?.meta_description ||
  pageData.value?.description ||
  'For more than 45 years, Spring Green has been providing professional lawn care, pest control and tree care services. Visit www.spring-green.com to learn more about our dedication to quality.'
);

useSeoHead({
  title: seoTitle.value,
  description: seoDescription.value,
  openGraph: {
    title: pageData.value?.title || seoTitle.value,
    description: seoDescription.value,
    image: heroImage.value,
    url: `${frontEndUrl}/learn/lawn-care-guides/`,
    type: 'website',
  },
  twitter: {
    title: pageData.value?.title || seoTitle.value,
    description: seoDescription.value,
    image: heroImage.value,
    card: heroImage.value ? 'summary_large_image' : undefined,
  },
  structuredData: topicsItemList.value,
  orgData: orgData.value,
  breadcrumbs: [
    { name: "Home", url: `${frontEndUrl}/` },
    { name: "Learn", url: `${frontEndUrl}/learn/` },
    { name: "Lawn Care Guides", url: `${frontEndUrl}/learn/lawn-care-guides/` },
  ],
});

onMounted(async () => {
  await $userContext.metaRefresh({
    refresh: new Set(["ORDER_INFO"]),
  });
});
</script>

<style scoped>

</style>
